import { DolfinDiagram } from '../diagram'
import { DolfinExecution } from '../execution'
import DolfinCompensationRulesFlowExecutor from '../flow-executor/compensation-rules'
import { NodeError, NodeWarning, TypedVariableOutput } from '../types'
import { DolfinNode } from './base'
import { TypedNode } from './types'

/**
 * Addition node - adds an amount to the current compensation value.
 *
 * Supports:
 * - `percentage` - Percentage of target outcome, metric, or column value
 * - `fixed` - Fixed amount
 *
 * @see /docs/domain/nodes/addition.md for canonical documentation
 */
export class AdditionNode extends DolfinNode<DolfinCompensationRulesFlowExecutor, 'addition'> {
  private getCompensationValuesPeriodCandidates(
    mapper: (compensationValue: { profile_id: string; value: number }) => {
      profile_id: string
      value: number
    }
  ): TypedVariableOutput<'addition'> {
    if (!this.node.data.parsedValues?.compensationValuesPeriodCandidates) {
      return []
    }

    return [
      {
        name: 'compensation_values_period_candidates',
        value: this.node.data.parsedValues.compensationValuesPeriodCandidates.map(mapper)
      }
    ]
  }

  private getCompensationValuePeriodCandidate(
    mapper: (compensationValue: number) => number
  ): TypedVariableOutput<'addition'> {
    if (this.node.data.parsedValues?.compensationValuePeriodCandidate === undefined) {
      return []
    }

    return [
      {
        name: 'compensation_value_period_candidate',
        value: mapper(this.node.data.parsedValues.compensationValuePeriodCandidate)
      }
    ]
  }

  async process(): Promise<TypedVariableOutput<'addition'>> {
    const formData = this.node.data.formData

    if (formData.type === 'percentage') {
      const targetRef = 'target' in formData ? formData.target : undefined
      const columnRef = 'column' in formData ? formData.column : undefined
      const metricRef = 'metric' in formData ? formData.metric : undefined
      const referenceType = 'reference_type' in formData ? formData.reference_type : undefined

      if (
        this.flowExecutor.compensationRule.outcome_period &&
        (!referenceType || referenceType === 'target') &&
        targetRef
      ) {
        const targetOutcomes = await this.flowExecutor.rpcClient.getPeriodTargetOutcomes(
          targetRef.id,
          this.flowExecutor.period!.start,
          this.flowExecutor.period!.end,
          this.flowExecutor.profileId!,
          this.flowExecutor.compensationRule.plan_id
        )

        if (this.node.data.parsedValues?.compensationValues) {
          return [
            {
              name: 'additions',
              value: (
                this.node.data.parsedValues.compensationValues as {
                  profile_id: string
                  value: number
                }[]
              ).map(compensationValue => ({
                profile_id: compensationValue.profile_id,
                value:
                  formData.percentage *
                  (targetOutcomes.find(
                    outcome => outcome.profile_id === compensationValue.profile_id
                  )?.outcome ?? 0)
              }))
            },
            {
              name: 'compensation_values',
              value: (
                this.node.data.parsedValues?.compensationValues as {
                  profile_id: string
                  value: number
                }[]
              ).map(compensationValue => ({
                profile_id: compensationValue.profile_id,
                value:
                  compensationValue.value +
                  formData.percentage *
                    (targetOutcomes.find(
                      outcome => outcome.profile_id === compensationValue.profile_id
                    )?.outcome ?? 0)
              }))
            },
            ...this.getCompensationValuesPeriodCandidates(compensationValue => ({
              profile_id: compensationValue.profile_id,
              value:
                compensationValue.value +
                formData.percentage *
                  (targetOutcomes.find(
                    outcome => outcome.profile_id === compensationValue.profile_id
                  )?.outcome ?? 0)
            }))
          ]
        } else {
          const addition =
            formData.percentage *
            (targetOutcomes.find(outcome => outcome.profile_id === this.flowExecutor.profileId)
              ?.outcome ?? 0)

          return [
            {
              name: 'addition',
              value: addition
            },
            {
              name: 'compensation_value',
              value: (this.node.data.parsedValues?.compensationValue ?? 0) + addition
            },
            ...this.getCompensationValuePeriodCandidate(
              compensationValue => compensationValue + addition
            )
          ]
        }
      } else if (
        !this.flowExecutor.compensationRule.outcome_period &&
        (!referenceType || referenceType === 'column') &&
        columnRef
      ) {
        const addition =
          formData.percentage *
          ((this.flowExecutor.recordData!.getFieldValue(
            columnRef.name as string,
            this.flowExecutor.customFields
          ) as number) ?? 0)

        return [
          {
            name: 'addition',
            value: addition
          },
          {
            name: 'compensation_value',
            value: (this.node.data.parsedValues?.compensationValue ?? 0) + addition
          },
          ...this.getCompensationValuePeriodCandidate(
            compensationValue => compensationValue + addition
          )
        ]
      } else if (
        this.flowExecutor.compensationRule.outcome_period &&
        (!referenceType || referenceType === 'metric') &&
        metricRef
      ) {
        const metricValue = await this.getMetricReferenceValue(
          metricRef,
          this.flowExecutor.period!.start,
          this.flowExecutor.period!.end
        )

        const addition = formData.percentage * metricValue

        if (this.node.data.parsedValues?.compensationValues) {
          return [
            {
              name: 'additions',
              value: (
                this.node.data.parsedValues.compensationValues as {
                  profile_id: string
                  value: number
                }[]
              ).map(compensationValue => ({
                profile_id: compensationValue.profile_id,
                value: addition
              }))
            },
            {
              name: 'compensation_values',
              value: (
                this.node.data.parsedValues?.compensationValues as {
                  profile_id: string
                  value: number
                }[]
              ).map(compensationValue => ({
                profile_id: compensationValue.profile_id,
                value: compensationValue.value + addition
              }))
            },
            ...this.getCompensationValuesPeriodCandidates(compensationValue => ({
              profile_id: compensationValue.profile_id,
              value: compensationValue.value + addition
            }))
          ]
        }

        return [
          {
            name: 'addition',
            value: addition
          },
          {
            name: 'compensation_value',
            value: (this.node.data.parsedValues?.compensationValue ?? 0) + addition
          },
          ...this.getCompensationValuePeriodCandidate(
            compensationValue => compensationValue + addition
          )
        ]
      }
    }

    const fixedAmount =
      'fixed_amount' in formData && typeof formData.fixed_amount === 'number'
        ? formData.fixed_amount
        : 0

    if (this.node.data.parsedValues?.compensationValues) {
      return [
        {
          name: 'addition',
          value: fixedAmount
        },
        {
          name: 'compensation_values',
          value: (
            this.node.data.parsedValues.compensationValues as {
              profile_id: string
              value: number
            }[]
          ).map(compensationValue => ({
            profile_id: compensationValue.profile_id,
            value: compensationValue.value + fixedAmount
          }))
        },
        ...this.getCompensationValuesPeriodCandidates(compensationValue => ({
          profile_id: compensationValue.profile_id,
          value: compensationValue.value + fixedAmount
        }))
      ]
    }

    return [
      {
        name: 'addition',
        value: fixedAmount
      },
      {
        name: 'compensation_value',
        value: (this.node.data.parsedValues?.compensationValue ?? 0) + fixedAmount
      },
      ...this.getCompensationValuePeriodCandidate(
        compensationValue => compensationValue + fixedAmount
      )
    ]
  }

  async parse(_: DolfinDiagram, execution?: DolfinExecution) {
    this.parseCompensationValues(execution)

    return this.node
  }

  static validate(
    node: TypedNode<'addition'>,
    diagram: DolfinDiagram
  ): {
    errors: NodeError[]
    warnings: NodeWarning[]
  } {
    const superValidation = super.validate(node, diagram)

    return {
      errors: [...superValidation.errors],
      warnings: [...superValidation.warnings]
    }
  }
}
